<template>
  <el-header
    height="72px"
    class="z-10 py-4 px-8 flex flex-wrap items-center gap-2 font-medium bg-th-white th:bg-th-text-primary uz:shadow-[0_0_12px] uz:shadow-black/[.12]"
  >
    <div class="h-[24px] w-[114px] uz:h-[20px] uz:w-[88px]">
      <th-image
        :src="logo"
        contain
        class="justify-self-start"
        position="left center"
        width="100%"
        height="100%"
      />
    </div>

    <div class="grow" />

    <onboarding v-if="!isUnifiedCommerceClient" />

    <help v-if="!isUnifiedCommerceClient" />
    <support-docs v-else />

    <notifications />

    <ai-chat v-if="canAccessAiChat" />

    <branch-context v-if="isUnifiedCommerceClient" />
    <location-context v-else />

    <el-dropdown
      ref="accountDropdown"
      trigger="click"
      placement="bottom-end"
      data-testid="two-fa-account-dropdown"
      @visible-change="onDropdownVisibilityChange"
    >
      <!-- Account name -->
      <el-button
        type="default"
        size="default"
        text
        class="th:text-th-white th:hover:bg-th-text-secondary"
        data-testid="two-fa-account-button"
      >
        {{ accountName }}
        <el-icon class="el-icon--right">
          <arrow-up-icon v-if="isDropDownVisible" />
          <arrow-down-icon v-else />
        </el-icon>
      </el-button>
      <template #dropdown>
        <el-dropdown-menu
          class="th-dropdown-menu"
          data-testid="two-fa-account-dropdown-menu"
        >
          <!-- Username -->
          <el-dropdown-item data-testid="two-fa-username-dropdown-item">
            <router-link
              v-if="isUnifiedCommerceClient"
              to="/me"
              class="user-profile-link"
              data-testid="two-fa-user-profile-link"
              @click="closeDropdown"
            >
              <span class="user-avatar" data-testid="two-fa-user-avatar">
                {{ userInitial }}
              </span>
              <span
                class="user-email"
                data-testid="two-fa-user-name"
                v-text="userName"
              />
            </router-link>
            <div v-else class="user-label-container">
              <div v-text="userName" />
              <div class="userId" v-text="userId" />
            </div>
          </el-dropdown-item>
          <el-dropdown-item
            v-if="
              isUnifiedCommerceClient &&
              hasMmsHierarchyFeature &&
              hasMultipleDashboards
            "
            data-testid="mms-switch-dashboard-dropdown-item"
          >
            <dashboard-switcher />
          </el-dropdown-item>
          <!-- Profile -->
          <el-dropdown-item v-if="isSubUserStaff">
            <router-link
              to="/user_profile"
              class="flex flex-row items-center justify-start"
            >
              {{ $t('nav.main.items.user_profile.title') }}
            </router-link>
          </el-dropdown-item>
          <!-- Cookie Preferences -->
          <el-dropdown-item
            v-if="isCookieScriptLoaded"
            class="justify-start"
            @click.prevent="showCookiePreferences"
          >
            {{ $t('nav.main.items.cookie_preferences.title') }}
          </el-dropdown-item>
          <!-- Exit -->
          <el-dropdown-item v-if="isPOS">
            <el-button text class="w-full justify-start" @click="exitDashboard">
              {{ $t('nav.main.items.exit_dashboard.title') }}
              <el-icon class="el-icon--right">
                <close-icon />
              </el-icon>
            </el-button>
          </el-dropdown-item>
          <!-- Logout -->
          <el-dropdown-item v-else>
            <router-link
              to="/logout"
              class="w-full flex items-center hover:text-th-primary justify-start"
            >
              {{ $t('nav.main.items.logout.title') }}

              <el-icon class="ml-2 fill-current">
                <logout-icon />
              </el-icon>
            </router-link>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </el-header>
</template>

<script>
import { useBranchesStore } from '@shared/store/branches'
import { getPageTranslationKey } from './header-helper'
import LocationContext from './location-context'
import BranchContext from '@uz/components/nav/branch-context'
import SupportDocs from '@uz/components/nav/support-docs'
import safeGet from 'just-safe-get'
import th from '@tillhub/javascript-sdk'
import { useAppConfigStore } from '@shared/store/app-config'
import { storeToRefs } from 'pinia'
import { useConfigurationStore } from '@shared/store/configuration'
import { useProductsStore } from '@shared/store/products'
import { useSocketIOStore } from '@shared/store/socket-io'
import { useAuthStore } from '@shared/store/auth'
import { registerSocketListeners } from '@shared/store/socket-listeners'
import { isUnifiedCommerce } from '@shared/constants'
import Onboarding from './actions/onboarding'
import Help from './actions/help'
import AiChat from './actions/ai-chat'
import Notifications from './actions/notifications'
import DashboardSwitcher from '@uz/components/nav/dashboard-switcher'
import { useConnectedDashboardsStore } from '@shared/store/connected-dashboards'
import { FeatureFlags, usePosthogFeatureFlag } from '@shared/plugins/posthog'
import { computed, inject, watch } from 'vue'
import {
  CloseIcon,
  LogoutIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@shared/assets/icons'
import { useWhitelabelThemeStore } from '@shared/store/whitelabel-theme.ts'

export default {
  components: {
    LocationContext,
    BranchContext,
    Onboarding,
    Help,
    AiChat,
    SupportDocs,
    Notifications,
    DashboardSwitcher,
    CloseIcon,
    LogoutIcon,
    ArrowUpIcon,
    ArrowDownIcon
  },
  setup() {
    const branchesStore = useBranchesStore()
    const appConfigStore = useAppConfigStore()
    const productsStore = useProductsStore()
    const themeStore = useWhitelabelThemeStore()
    const authStore = useAuthStore()
    const connectedDashboardsStore = useConnectedDashboardsStore()

    const { navigation } = storeToRefs(appConfigStore)
    const { logo } = storeToRefs(themeStore)

    const {
      isAdmin,
      authenticated,
      staff,
      thUserId: subUser
    } = storeToRefs(authStore)

    const { currentDashboard, hasMultipleDashboards } = storeToRefs(
      connectedDashboardsStore
    )

    const userName = computed(() => authStore.username || '-')
    const accountName = computed(
      () => currentDashboard.value?.name || authStore.accountName || '-'
    )
    const userId = computed(() => authStore.user || '-')
    const userInitial = computed(() => {
      const match = (authStore.username || '').match(/[a-z0-9]/i)
      return match ? match[0].toUpperCase() : '?'
    })
    const checkPermissions = inject('checkPermissions')
    const isFeatureEnabled = inject('isFeatureEnabled')
    const canAccessAiChat = computed(
      () =>
        isFeatureEnabled('agent_chat') &&
        checkPermissions({
          scopes: ['agent-chat:read', 'agent-chat:create', 'agent-chat:update']
        })
    )

    const hasMmsHierarchyFeature = usePosthogFeatureFlag(
      FeatureFlags.UOD_MMS_HIERARCHY
    )
    let connectedDashboardsLoaded = false

    const loadConnectedDashboards = async () => {
      await authStore.waitThInitializated()
      await connectedDashboardsStore.loadConnectedDashboards()
      connectedDashboardsLoaded = true
    }

    watch(
      hasMmsHierarchyFeature,
      async (enabled) => {
        if (!enabled || connectedDashboardsLoaded) {
          return
        }

        await loadConnectedDashboards()
      },
      { immediate: true }
    )

    return {
      navigation,
      branchesStore,
      productsStore,
      authStore,
      connectedDashboardsStore,
      authenticated,
      isAdmin,
      staff,
      subUser,
      userName,
      accountName,
      userId,
      userInitial,
      canAccessAiChat,
      hasMultipleDashboards,
      hasMmsHierarchyFeature,
      logo
    }
  },
  data() {
    return {
      userInfoMode: 'human-readable',
      isDropDownVisible: false,
      heartbeat: null,
      isPOS: false,
      isCookieScriptLoaded: false
    }
  },
  computed: {
    isSubUserStaff() {
      return !!this.staff && !!this.subUser
    },
    isUnifiedCommerceClient() {
      return isUnifiedCommerce()
    },
    getPageName() {
      const translationKey = getPageTranslationKey(
        this.navigation,
        this.$route.path
      )
      return this.$t(translationKey)
    }
  },
  async created() {
    await this.authStore.waitThInitializated()
    this.setPOSStatus()
    this.startHeartbeat()
    this.checkForCookieScript()
    this.productsStore.checkProductsCount()
    this.branchesStore.checkBranchesCount()
    await useSocketIOStore().initConnection(this)
    registerSocketListeners(this)
    useConfigurationStore().loadBranchCurrencies()
  },
  beforeUnmount() {
    this.stopHeartbeat()
    useSocketIOStore().closeConnection(this)
  },
  methods: {
    onDropdownVisibilityChange(isVisible) {
      this.isDropDownVisible = isVisible
    },
    checkForCookieScript() {
      if (!window.CookieScript?.instance) {
        window.addEventListener('CookieScriptLoaded', () => {
          this.isCookieScriptLoaded = true
        })
      } else {
        this.isCookieScriptLoaded = true
      }
    },
    exitDashboard() {
      if (safeGet(window, 'webkit.messageHandlers.close.postMessage')) {
        this.authStore.logout()
        window.webkit.messageHandlers.close.postMessage('exit_dashboard')
        th.destroy()
      }
    },
    setPOSStatus() {
      try {
        const clientApp =
          JSON.parse(window.localStorage.getItem('clientApp')) || {}
        this.isPOS = !!safeGet(clientApp, 'embed')
      } catch (error) {
        this.$logException(error, { trackError: true })
      }
    },
    startHeartbeat() {
      // Will only start heartbeat if dashboard is in POS system
      if (!this.isPOS) return
      this.updateHeartbeat()
      this.heartbeat = setInterval(this.updateHeartbeat, 10000)
    },
    updateHeartbeat() {
      const timeStamp = new Date().getTime()
      // This post message eto POS to let it know the
      // to let it know the dashboard is still active
      if (safeGet(window, 'webkit.messageHandlers.heartbeat.postMessage')) {
        window.webkit.messageHandlers.heartbeat.postMessage(timeStamp)
      }
    },
    stopHeartbeat() {
      if (!this.isPOS) return
      clearInterval(this.heartbeat)
    },
    showCookiePreferences() {
      window.CookieScript?.instance?.show()
    },
    closeDropdown() {
      this.$refs.accountDropdown?.handleClose()
    }
  }
}
</script>

<style scoped>
.user-label-container {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-direction: column;
  line-height: normal;
  cursor: default;
}

.user-profile-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--Palette-Plum-Plum-100, #0c1332);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.user-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-label-container .userId {
  font-size: 0.7em;
  opacity: 0.5;
}

.th-dropdown-menu :deep(.el-dropdown-menu__item:not(:first-child)) {
  border-top: 1px solid var(--border-color);
}
</style>
